// src/pages/AboutPage.js
import React from 'react';
import {
  Box,
  Heading,
  Text,
  VStack,
  Flex,
  Image,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  useColorModeValue,
  Button,
} from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import mission from '../assets/mission.png';
import building_2 from '../assets/building_2.png';
import building_3 from '../assets/group_celebration.jpg';

// A reusable component for the alternating text/image sections
const FeatureSection = ({ title, text, imageUrl, reverse = false }) => {
  return (
    <Flex
      direction={{ base: 'column', md: reverse ? 'row-reverse' : 'row' }}
      align="center"
      gap={12}
      py={12}
      w="full"
    >
      <Box flex="1">
        <Image src={imageUrl} alt={title} borderRadius="xl" boxShadow="lg" />
      </Box>
      <VStack flex="1" align="flex-start" justify="center" spacing={5}>
        <Heading as="h2" size="xl" color="orange.500">
          {title}
        </Heading>
        <Text fontSize="lg" color="gray.600" lineHeight="tall">
          {text}
        </Text>
      </VStack>
    </Flex>
  );
};

export default function AboutPage() {
  const bgColor = useColorModeValue('gray.50', 'gray.900');

  return (
    <>
      <Header />
      <Box as="main" bg={bgColor}>
        <VStack maxW="7xl" mx="auto" px={8} spacing={12} py={{ base: 16, md: 24 }}>
          {/* Page Header */}
          <VStack spacing={4} textAlign="center">
            <Breadcrumb>
              <BreadcrumbItem>
                <BreadcrumbLink as={RouterLink} to="/">Home</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbItem isCurrentPage>
                <BreadcrumbLink href="#">About Us</BreadcrumbLink>
              </BreadcrumbItem>
            </Breadcrumb>
            <Heading as="h1" size="2xl" fontWeight="extrabold">
              Our Guiding Principles
            </Heading>
            <Text maxW="3xl" color="gray.600">
              We are defined by an unwavering commitment to our core principles. They are the foundation of our company, guiding every decision, every project, and every partnership we build.
            </Text>
          </VStack>

          {/* Mission Section */}
          <FeatureSection
            title="Our Mission"
            text="To deliver high-quality, cost-effective projects on schedule by employing and supporting a motivated, flexible, and focused team. We value our relationships and will remain fair and true in all our dealings."
            imageUrl={mission}
          />

          {/* Vision Section */}
          <FeatureSection
            title="Our Vision"
            text="To be the most trusted and sought-after construction company in the region, known for our commitment to excellence, innovation, and the development of our communities and our people."
            imageUrl={building_2}
            reverse={true} // This flips the image to the other side
          />

          {/* Values Section */}
          <FeatureSection
            title="Our Values"
            text="Integrity, Quality, and Safety are the pillars of our work. We are dedicated to building lasting structures and relationships based on trust, mutual respect, and a commitment to shared success."
            imageUrl={building_3}
          />

          {/* Call to Action */}
          <VStack spacing={6} textAlign="center" py={12}>
            <Heading as="h2" size="xl">Let's Build Together</Heading>
            <Text maxW="2xl">
              Ready to start your next project with a team that's as invested in your success as you are? Reach out to us today.
            </Text>
            <Button as={RouterLink} to="/contact" colorScheme="orange" size="lg">
              Contact Us
            </Button>
          </VStack>
        </VStack>
      </Box>
      <Footer />
    </>
  );
}